import { useSelector } from 'react-redux';
import MenuRow from './MenuRow';
import NoOptions from './NoOptions';
import { HintIcon } from '@shared/ui';
import { getTitle } from '../helpers';
import { CONFLICT_KINDS, findConflict } from '../lib/conflicts';

/**
 * Right pane: what the user has picked, in two groups — included values
 * and excluded ones.
 *
 * A group with nothing in it is not rendered at all, headers included.
 * That keeps the common single-sided case looking exactly as it did
 * before exclusion existed, which matters here more than usual: the
 * popover is already `min-width: 760px`, an iPad in portrait is 768pt,
 * so vertical space is the only space left to spend.
 *
 * Behaviour preserved 1:1: row click removes the option from its own
 * group, the lightning-bolt button applies the current draft.
 */
const ClearIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const WarningIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const SelectedSection = ({ draft, onRemove, handleClose, clearAllFilter }) => {
  const { tables, btn, scit, slit, formulas } = useSelector(state => state.i18n);
  const ruleHint =
    tables?.filterRuleExplainer ||
    'Included values are matched first, then excluded ones are removed from that set. With nothing included, an exclusion means everything except those values. Rows with no value at all are kept.';
  const { include = [], exclude = [] } = draft || {};
  const hasSelectedOptions = include.length > 0 || exclude.length > 0;

  // Named, not merely counted: "2 values are in both lists" makes the
  // user hunt for which two.
  const conflict = findConflict(draft);
  const conflictNames = conflict.values.length
    ? getTitle(conflict.values, { scit, slit, formulas, tables })
    : '';

  const renderGroup = (list, listName, label, modifier) => {
    if (!list.length) return null;
    return (
      <div className="qhs-dynh-group">
        <div className={`qhs-dynh-group__head qhs-dynh-group__head--${modifier}`}>
          {label}
          <span className="qhs-dynh-section-head__count">{list.length}</span>
        </div>
        <div className="qhs-dynh-list qhs-dynh-list--group" role="listbox">
          {list.map(option => (
            <MenuRow
              key={option}
              option={option}
              state={listName}
              onItemClick={opt => onRemove(opt, listName)}
              applyCurrentFilter={e => handleClose(e, option)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="qhs-dynh-pane">
      <div className="qhs-dynh-section-head">
        <span className="qhs-dynh-section-head__label">
          {tables?.selectedOptions || 'Selected options'}
          <HintIcon hint={ruleHint} />
        </span>
        <button
          type="button"
          className="qhs-dynh-section-head__action"
          onClick={clearAllFilter}
          disabled={!hasSelectedOptions}
        >
          <ClearIcon />
          {btn?.clearFilter || 'Clear'}
        </button>
      </div>
      {conflict.kind !== CONFLICT_KINDS.none && (
        <p className={`qhs-dynh-conflict qhs-dynh-conflict--${conflict.kind}`} role="status">
          <WarningIcon />
          <span>
            {conflict.kind === CONFLICT_KINDS.empty
              ? `${conflictNames} — ${
                  tables?.conflictEmpty ||
                  'is in both lists, so the two rules cancel out and the table will be empty.'
                }`
              : `${conflictNames} — ${
                  tables?.conflictPartial ||
                  'is in both lists and will be left out; the other included values still show.'
                }`}
          </span>
        </p>
      )}
      {hasSelectedOptions ? (
        <div className="qhs-dynh-groups">
          {renderGroup(include, 'include', tables?.includeGroup || 'Include', 'include')}
          {renderGroup(exclude, 'exclude', tables?.excludeGroup || 'Exclude', 'exclude')}
        </div>
      ) : (
        <NoOptions title={tables?.noSelectedOptions || 'Nothing selected yet'} />
      )}
    </div>
  );
};

export default SelectedSection;
